import { createAsyncThunk } from '@reduxjs/toolkit';

import { getAuthToken } from '../utils/authUtils';
import { withRequestDedupe } from './requestDedupe';

/**
 * Creates a thunk that receives a Firebase ID token as its first argument.
 *
 * This replaces eleven byte-identical copies of the same factory, one per
 * slice. Each copy opened a fresh `onAuthStateChanged` subscription per
 * dispatch and wrapped the whole thunk body in `new Promise(...)` just to
 * deliver a token - which is why `api.getState` and `api.signal` were
 * structurally unreachable across the entire data layer.
 *
 * `getAuthToken()` reads from a single app-lifetime listener instead, and
 * Firebase caches the token internally, so this costs nothing on the network.
 *
 * Behaviour is preserved exactly, with one intentional simplification: the old
 * factory had a separate `onAuthStateChanged` error callback that rejected with
 * the Firebase error message. `getAuthToken()` returns null on that path, so it
 * now rejects with `'User not authenticated'` like the signed-out case. Both
 * were already indistinguishable to callers - every one of them only checks
 * that the thunk rejected.
 *
 * The wrapped function is still called as `fn(token, args, api)`; the one
 * factory that declared `fn(token, args)` simply ignores the third argument.
 *
 * @template T
 * @param {string} name action type prefix, e.g. `'deals/fetchDeals'`
 * @param {(token: string, args: any, api: object) => Promise<T>} fn the thunk body
 * @param {object} [options] forwarded to `createAsyncThunk`, plus the extra
 *   `dedupeKey` below
 * @param {(args: any) => (string|null)} [options.dedupeKey] when given,
 *   concurrent dispatches producing the same key share one request. Useful for
 *   status endpoints several components poll at once. Return `null` to opt a
 *   particular call out. See `redux/requestDedupe`.
 * @returns {import('@reduxjs/toolkit').AsyncThunk<T, any, {}>} the thunk
 */
export const createAuthedThunk = (name, fn, options = {}) => {
  const { dedupeKey, ...asyncThunkOptions } = options;

  const creator = async (args, api) => {
    const token = await getAuthToken();
    if (!token) return api.rejectWithValue('User not authenticated');
    try {
      return await fn(token, args, api);
    } catch (e) {
      return api.rejectWithValue(e?.response?.data || e.message);
    }
  };

  return createAsyncThunk(
    name,
    dedupeKey ? withRequestDedupe(dedupeKey, creator) : creator,
    asyncThunkOptions,
  );
};

export default createAuthedThunk;
